import { type ActionEvent, Controller } from "@hotwired/stimulus"
import Supercluster, { type AnyProps, type ClusterFeature, type PointFeature } from "supercluster"

import { SCROLL_DOWN_CLASS } from "@/components/content/HideAndSeek"
import { type ImageSlide } from "@/controllers/search/map_marker_carousel_controller"
import { hideAllLoaders } from "@/helpers/page_loader"
import { pluralize } from "@/helpers/pluralize"
import { debounce } from "@/helpers/utils"

declare global {
  interface Window {
    mapPins: MapPin[]
  }
}

interface MapPin {
  firstCarouselImage: ImageSlide
  id: string
  name: string
  path: string
  position: google.maps.LatLngLiteral
  premiumLogo?: string
  promoOffersText?: string
  price: string
  originalPrice?: string
  overallRating?: {
    badgeClasses: string
    reviewDescription: string
    reviewRating: number
    numberOfReviews: number
  }
}

type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement & {
  // Avoid having to cast `Node` to `HTMLDivElement` everywhere
  content: HTMLDivElement
}

const ACTIVE_CLASS = "--active" as const
const ACTIVE_ZINDEX = 2000000 // Ensure markers appear above clusters
const HIDDEN = "hidden"
const HIGHLIGHTED_CLASS = "--highlighted" as const
const MAP_OPEN_CLASS = "--map-open"
const RESULTS_COLLAPSED_CLASS = "--results-collapsed"
const SEARCH_BAR_HEIGHT_CSS_PROP = "--search-bar-height"
const PROMO_BAR_HEIGHT_CSS_PROP = "--promo-bar-height"
const SESSION_STORAGE_KEYS = Object.freeze({
  MAP_OPEN: "search.map.map_open",
  RESULTS_OPEN: "search.map.results_open"
} as const)

export default class SearchMap extends Controller {
  static targets = ["markerContent", "map"]
  declare readonly markerContentTarget: HTMLElement
  declare readonly mapTarget: HTMLElement

  static values = {
    mapOpen: { type: Boolean, default: false },
    resultsOpen: { type: Boolean, default: false }
  }
  declare mapOpenValue: boolean
  declare resultsOpenValue: boolean

  declare private static MarkerLibrary: google.maps.MarkerLibrary

  declare private googleMap: google.maps.Map
  private markers: AdvancedMarkerElement[] = []
  declare private supercluster: Supercluster

  initialize(): void {
    this.mapOpenValue = window.sessionStorage.getItem(SESSION_STORAGE_KEYS.MAP_OPEN) !== null
    this.resultsOpenValue =
      window.sessionStorage.getItem(SESSION_STORAGE_KEYS.RESULTS_OPEN) !== null
  }

  async mapOpenValueChanged(): Promise<void> {
    if (this.mapOpenValue) {
      if (!this.googleMap) {
        await this.initGoogleMap()
      }

      window.scrollTo(0, 0)
      this.setSearchBarHeightForCSS()
      this.mapTarget.removeAttribute(HIDDEN)
      document.body.classList.add(MAP_OPEN_CLASS)
      document.body.classList.remove(SCROLL_DOWN_CLASS)
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.MAP_OPEN, "")
    } else {
      this.deactivateMarkers()
      this.mapTarget.setAttribute(HIDDEN, "")
      document.body.classList.remove(MAP_OPEN_CLASS, RESULTS_COLLAPSED_CLASS)
      window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.MAP_OPEN)
    }

    this.replaceHistoryWithMapState()
    hideAllLoaders()
  }

  resultsOpenValueChanged(): void {
    // Toggle class on body instead of relevant element to prevent it getting removed by
    // Turbo stream response.
    document.body.classList.toggle(RESULTS_COLLAPSED_CLASS, this.resultsOpenValue)

    if (this.resultsOpenValue) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.RESULTS_OPEN, "")
    } else {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.RESULTS_OPEN)
    }
  }

  deactivateMarkers(): void {
    this.pinMarkers.forEach((marker) => this.toggleCardContent(marker))
  }

  hide(): void {
    this.mapOpenValue = false
    this.resultsOpenValue = false
    this.pushHistoryWithMapState()
  }

  onPopState({ state }: PopStateEvent): void {
    // This function runs for any `popstate` on the page, not just map-related.
    if (state?.mapOpen === undefined) {
      return
    }

    if (state.mapOpen !== this.mapOpenValue) {
      this.mapOpenValue = !this.mapOpenValue
    }
  }

  onViewportChange(): void {
    this.setSearchBarHeightForCSS()
  }

  async refreshMarkers(): Promise<void> {
    // This function is also called on `#turbo-filters-*` frame loads, which can obviously happen
    // before the map is ever initialized.
    if (!this.googleMap) {
      return
    }

    this.supercluster.load(this.featurePoints)
    this.setMarkersInBounds()

    if (this.markers.length) {
      const bounds = await this.boundsFromClusters()
      this.googleMap.fitBounds(bounds)
    }
  }

  replaceHistoryWithMapState(): void {
    window.history.replaceState({ ...window.history.state, mapOpen: this.mapOpenValue }, "")
  }

  show(): void {
    this.mapOpenValue = true
    this.pushHistoryWithMapState()
  }

  toggleHighlightMarker({ params: { venueId } }: ActionEvent): void {
    const marker = this.pinMarkers.find(
      (marker) => marker.content.dataset.venueId === venueId.toString()
    )

    if (marker) {
      this.toggleMarkerState(marker, HIGHLIGHTED_CLASS)
    }
  }

  toggleResultsList(): void {
    this.resultsOpenValue = !this.resultsOpenValue
  }

  private activateMarker(marker: AdvancedMarkerElement, pin: MapPin): void {
    const cardContent = this.cardContent(pin)

    this.toggleCardContent(marker, cardContent)
  }

  private appendHideControl(): void {
    const button = document.createElement("button")
    button.className = "ui-component-button variant:input search__map__hide-map"
    button.innerHTML = `<i class="far fa-xmark fa:before" aria-hidden="true"></i> Hide map`
    button.type = "button"

    button.addEventListener("click", this.hide.bind(this))
    this.googleMap.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(button)
  }

  private async boundsFromClusters(): Promise<google.maps.LatLngBounds> {
    const { LatLngBounds } = (await window.google.maps.importLibrary(
      "core"
    )) as google.maps.CoreLibrary
    const { west, south, east, north } = LatLngBounds.MAX_BOUNDS.toJSON()
    const bounds = new LatLngBounds()

    this.supercluster
      .getClusters([west, south, east, north], this.googleMap.get("maxZoom"))
      .forEach((point) => {
        const [lng, lat] = point.geometry.coordinates
        bounds.extend({ lng, lat })
      })

    return bounds
  }

  private cardContent(pin: MapPin): string {
    return `
      <div class="map-marker-card">
        ${
          pin.promoOffersText
            ? `<div class="label-sm text-center uppercase text-white p-4">
                ${pin.promoOffersText}
              </div>`
            : ""
        }
        <div>
          <div data-img class="relative">
            <div
              data-controller="map-marker-carousel"
              data-map-marker-carousel-id-value="${pin.id}"
              data-map-marker-carousel-image-value='${pin.firstCarouselImage}'
            ></div>
            ${
              pin.premiumLogo
                ? `<img alt="Elysium Collection" src="${pin.premiumLogo}" width="84" height="28">`
                : ""
            }
          </div>
          <section>
            <header class="flex flex-col gap-8 xs:mr-24">
              <a href="${pin.path}" class="no-underline heading-xs" target="_blank">
                ${pin.name}
              </a>
              ${
                pin.overallRating
                  ? `
                    <section class="flex items-center gap-4">
                      <span class="${pin.overallRating.badgeClasses}">
                        ${pin.overallRating.reviewRating}
                      </span>
                      <span class="text-primary text-sm font-medium">
                        ${pin.overallRating.reviewDescription}
                      </span>
                      <span class="text-secondary text-xs">
                        (${pluralize(pin.overallRating.numberOfReviews, "review")})
                      </span>
                    </section>
                    `
                  : ""
              }
            </header>
            <footer>
              <span class="text-secondary body-xs whitespace-nowrap">From</span>
              <div class="whitespace-nowrap flex items-center gap-4">
                ${
                  pin.originalPrice
                    ? `<span class="text-secondary body-sm line-through">${pin.originalPrice}</span>`
                    : ""
                }
                <span class="text-primary heading-sm">${pin.price}</span>
              </div>
              <span class="text-secondary body-xs whitespace-nowrap">Per person</span>
            </footer>
          </section>
          <button class="btn-dismiss absolute top-8 leading-12 right-8 z-10 p-0 after:bg-primary after:rounded-full after:text-16 after:px-6" data-action="search-map#deactivateMarkers:stop">
            <span>Close</span>
          </button>
        </div>
      </div>
    `
  }

  private clusterMarker(cluster: ClusterFeature<AnyProps>): AdvancedMarkerElement {
    const [lng, lat] = cluster.geometry.coordinates

    const content = document.createElement("div")
    content.className = "search__map__cluster"
    content.textContent = cluster.properties.point_count.toString()

    const marker = new SearchMap.MarkerLibrary.AdvancedMarkerElement({
      content,
      map: this.googleMap,
      position: { lat, lng },
      title: `${pluralize(cluster.properties.point_count, "spa")} in this area`
    }) as AdvancedMarkerElement

    marker.addListener("click", () => {
      const expandedZoomLevel = this.supercluster.getClusterExpansionZoom(+cluster.id!)
      this.googleMap.setZoom(expandedZoomLevel)
      this.googleMap.panTo(marker.position!)
    })

    return marker
  }

  private get clustersInBounds(): Array<ClusterFeature<AnyProps> | PointFeature<AnyProps>> {
    const bounds = this.googleMap.getBounds()!
    const southWest = bounds.getSouthWest()
    const northEast = bounds.getNorthEast()

    return this.supercluster.getClusters(
      [southWest.lng(), southWest.lat(), northEast.lng(), northEast.lat()],
      this.googleMap.getZoom()!
    )
  }

  private get featurePoints(): PointFeature<AnyProps>[] {
    return window.mapPins.map((pin) => ({
      type: "Feature",
      properties: {
        cluster: false,
        id: pin.id,
        pin
      },
      geometry: {
        type: "Point",
        coordinates: [pin.position.lng, pin.position.lat]
      }
    }))
  }

  private async initGoogleMap(): Promise<void> {
    const { importLibrary } = window.google.maps
    const { Map } = (await importLibrary("maps")) as google.maps.MapsLibrary
    const OPTIONS = {
      center: { lat: 53.5064506, lng: -2.3200198 }, // maps.google.co.uk default
      clickableIcons: false,
      fullscreenControl: false,
      gestureHandling: "greedy", // Move map with one finger instead of two on mobile
      mapId: "dcee0ac2d8a57685",
      maxZoom: 18,
      minZoom: 6,
      zoom: 6,
      disableDefaultUI: true
    }

    SearchMap.MarkerLibrary = (await importLibrary("marker")) as google.maps.MarkerLibrary
    this.googleMap = new Map(document.getElementById("js-google-map")!, OPTIONS)
    this.supercluster = new Supercluster({ radius: 60 })

    this.appendHideControl()

    // Wait until map is idle before refreshing markers for the first time.
    window.google.maps.event.addListenerOnce(this.googleMap, "idle", this.refreshMarkers.bind(this))

    // To update markers we use two listeners on different events instead of a single one on
    // `idle`, because we use `panTo` when a pin is clicked which then causes `idle` to fire and
    // we don't want to refresh markers in that case. Delay/debounce to help reduce flickering.
    this.googleMap.addListener("dragend", () =>
      window.setTimeout(this.setMarkersInBounds.bind(this), 50)
    )
    this.googleMap.addListener("zoom_changed", debounce(this.setMarkersInBounds.bind(this), 250))

    // Clicks on map should deactivate open markers.
    this.googleMap.addListener("click", this.deactivateMarkers.bind(this))
  }

  private pinMarker(pin: MapPin): AdvancedMarkerElement {
    const content = document.createElement("div")
    content.className = "map-marker group marker-wedge"
    content.dataset.venueId = pin.id
    content.innerHTML = this.pinMarkerContent(pin)

    const marker = new SearchMap.MarkerLibrary.AdvancedMarkerElement({
      content,
      map: this.googleMap,
      position: pin.position,
      title: pin.name
    }) as AdvancedMarkerElement

    marker.addListener("click", () => {
      const isActive = marker.content.classList.contains(ACTIVE_CLASS)
      if (isActive) {
        return
      }

      // Deactivate any other active markers
      this.deactivateMarkers()

      // Activate clicked marker if it was previously deactivated
      this.activateMarker(marker, pin)
    })

    return marker
  }

  private pinMarkerContent(pin: MapPin): string {
    return `
      <div class="marker-pin">
        <div class="marker-wedge">
          ${
            pin.overallRating
              ? `
                <div class="${pin.overallRating.badgeClasses} group-[.--active]:bg-white group-[.--active]:text-primary">
                  ${pin.overallRating.reviewRating}
                </div>
                `
              : ""
          }
          <span>${pin.price}</span>
        </div>
      </div>
    `
  }

  private get pinMarkers(): AdvancedMarkerElement[] {
    // Get non-cluster markers, i.e. pin markers.
    return this.markers.filter((marker) => !!marker.content.dataset?.venueId)
  }

  private pushHistoryWithMapState(): void {
    window.history.pushState({ ...window.history.state, mapOpen: this.mapOpenValue }, "")
  }

  private get searchBarElement(): HTMLElement {
    const NEW_SEARCH_BAR = document.body.getAttribute("data-new-search-bar") === "true"

    // Get search bar on the fly instead of on initialization to avoid potential race
    // conditions with Turbo.
    return NEW_SEARCH_BAR
      ? document.getElementById("js-search-bar")!
      : document.getElementById("elm-search-container")!
  }

  private setMarkersInBounds(): void {
    // Deactivate all markers before setting new ones.
    this.deactivateMarkers()
    // Save existing markers to remove after adding new ones, to reduce flickering.
    const currentMarkers = [...this.markers]

    // Set new markers.
    this.markers = this.clustersInBounds.map((cluster) =>
      cluster.properties.pin
        ? this.pinMarker(cluster.properties.pin)
        : this.clusterMarker(cluster as ClusterFeature<AnyProps>)
    )

    // Clear previous markers. A zero-delay setTimeout helps reduce flickering.
    window.setTimeout(() => {
      currentMarkers.forEach((marker) => (marker.map = null))
    }, 0)
  }

  private setSearchBarHeightForCSS(): void {
    window.requestAnimationFrame(() => {
      const searchDomRect = this.searchBarElement.getBoundingClientRect()
      const promoDomRect = document.getElementById("js-sitewide-promo")?.getBoundingClientRect()

      document.body.style.setProperty(SEARCH_BAR_HEIGHT_CSS_PROP, `${searchDomRect.height}px`)
      document.body.style.setProperty(PROMO_BAR_HEIGHT_CSS_PROP, `${promoDomRect?.height || 0}px`)
    })
  }

  private toggleCardContent(
    marker: AdvancedMarkerElement,
    cardContent = ""
  ): AdvancedMarkerElement {
    this.markerContentTarget.innerHTML = cardContent
    this.toggleMarkerState(marker, ACTIVE_CLASS, !!cardContent)

    return marker
  }

  private toggleMarkerState(
    marker: AdvancedMarkerElement,
    stateClass: typeof ACTIVE_CLASS | typeof HIGHLIGHTED_CLASS,
    force?: boolean
  ): void {
    marker.content.classList.toggle(stateClass, force)

    // Marker can be both active and highlighted at the same time, so check both before
    // setting z-index.
    const isStateOn = [ACTIVE_CLASS, HIGHLIGHTED_CLASS].some((token) =>
      marker.content.classList.contains(token)
    )

    marker.zIndex = isStateOn ? ACTIVE_ZINDEX : 0
  }
}
